import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {css} from '@emotion/css';

import Map from "../Map/Map";
import IconTool from '../IconTool/PointPanel/IconTool';
import ResultPanel from '../ResultPanel/ResultPanel';
import LayerOverview from '../Legend/LayerOverview';
import PointLayerMenu from '../IconTool/LayerPanel/PointLayerMenu';
import {DeleteConfirmationModal, SessionExpirationModal} from "../../common/Modals/MainMap/MainMapModal";

import {useInitStaticLayers} from "../../../queries/useStaticLayerQuery";
import {useInitPoint} from "../../../queries/usePointsQuery";
import {useMeshLayerQuery} from "../../../queries/useMeshLayerQuery";
import {useSessionManager} from "../../../hooks/useSessionManager";
import {useMapPopups} from "../Map/hooks/useMapPopups";
import {useMapLayers} from "../Map/hooks/useMapLayers";

import {useActiveMarker, useMarkerStore} from "../../../store/markerStore";
import useLegendStore from "../../../store/legendStore";
import useHomeStore from "../../../store/homeStore";
import useDatasetStore from "../../../store/datasetStore";
import useAuthStore from "../../../store/authStore";
import useMapStore from "../../../store/mapStore";
import {useMapLayerStore} from "../../../store/layerStore";

import LoadingContext from '../../../context/loadingContext/LoadingContext';
import {withLoading} from "../../common/Loading/withLoading";

import {IDatasetBrief} from '../../../types/metadata';
import {SEARCHPARAM} from "../../../utils/common";

import styles from './AppContainer.module.scss';
import useCartStore from "../../../store/cartStore";

export default function AppContainer() {
  const user = useAuthStore(state => state.user);

  const showMesh = useLegendStore(state => state.showMesh);

  const datasetBriefs = useDatasetStore(state => state.datasetBriefs);

  const datasetZones = useMapLayerStore(state => state.datasetZones);

  const shoppingItems = useCartStore(state => state.shoppingItems);

  const coordsMoved = useMapStore(state => state.coordsMoved);
  const setCoordsMoved = useMapStore(state => state.setCoordsMoved);

  const showResultCollapse = useHomeStore(state => state.showResultCollapse);
  const setShowResultCollapse = useHomeStore(state => state.setShowResultCollapse);

  const markers = useMarkerStore(state => state.markers);
  const activeMarker = useActiveMarker();
  const setActiveMarker = useMarkerStore(state => state.setActiveMarker);
  const updateMarker = useMarkerStore(state => state.updateMarker);
  const isAddMarkerMode = useMarkerStore(state => state.isAddMarkerMode);
  const setAddMarkerMode = useMarkerStore(state => state.setAddMarkerMode);

  const [selectedDatasetId, setSelectedDatasetId] = useState('');
  const [legendStartEndValues, setLegendStartEndValues] = useState<[number | null, number | null]>([null, null]);

  const loadingContext = useContext(LoadingContext);
  const location = useLocation();
  const didMountRef = useRef(false);
  const point = activeMarker;

  useInitStaticLayers();
  useInitPoint();
  const {
    meshUrl,
    setMeshUrl,
    meshResults,
    setMeshResults
  } = useMeshLayerQuery(selectedDatasetId, showMesh);

  const { isModalOpen, handleLogin } = useSessionManager(user);
  const {
    confirmationDeletePopup,
    setConfirmationDeletePopup,
    notAuthorizedUserPopup,
    setNotAuthorizedUserPopup,
    confirmationDeletePopupInsitu,
    setConfirmationDeletePopupInsitu,
    loadingDelete,
    expandedPoints,
    handleDeleteDataset,
    handleDeleteDatasetInsitu,
    handleRequestDeleteDataset,
    handlePopupCoordinatesChanged,
    handleClosePopup,
    popupContent,
    activePopupCoordinates,
    triggerMapClick
  } = useMapPopups(shoppingItems, setMeshUrl);

  const { layer } = useMapLayers(meshUrl, setMeshUrl, expandedPoints);

  const toggleResultCollapse = useCallback((bool: boolean, _checked: boolean = true) => {
    setShowResultCollapse(!bool);
  }, [setShowResultCollapse]);

  const onResetCoordsMoved = useCallback(() => {
    setCoordsMoved(false);
    if (point) {
      updateMarker(point.id, {coordinatesToMove: undefined});
    }
  }, [point, setCoordsMoved, updateMarker]);

  const onAddCoords = useCallback((coords: number[], fwePointName?: string) => {
    withLoading(() => triggerMapClick(coords, fwePointName), loadingContext, {
      source: 'onAddCoords',
    });
  }, [triggerMapClick, loadingContext]);

  const resolvedPopupContent = useMemo(() => {
    if (!activePopupCoordinates()) return null;
    return popupContent();
  }, [popupContent, activePopupCoordinates]);

  const activeCoordinates = useMemo(() => {
    return activePopupCoordinates();
  }, [activePopupCoordinates]);

  // Update result panel collapse based on markers or add marker mode
  useEffect(() => {
    setShowResultCollapse(isAddMarkerMode || (markers?.length > 0));
  }, [markers?.length, isAddMarkerMode, setShowResultCollapse]);

  // Handle URL parameters for initial coordinates
  useEffect(() => {
    if (didMountRef.current || !datasetBriefs.length) return;

    const searchParams = new URLSearchParams(location.search);
    const coordinate = searchParams.get(SEARCHPARAM.COOR);

    if (coordinate) {
      const fwePointName = searchParams.get(SEARCHPARAM.FWEPOINTNAME);

      if (fwePointName && markers.length > 0) {
        const existingMarker = markers.find(marker => marker.fwePointName === fwePointName);
        if (existingMarker) {
          setActiveMarker(existingMarker.id);
        }
      }

      let decodedCoor = coordinate;
      let previousDecodedCoor: string;

      do {
        previousDecodedCoor = decodedCoor;
        decodedCoor = decodeURIComponent(decodedCoor);
      } while (decodedCoor !== previousDecodedCoor);

    }

    didMountRef.current = true;
  }, [datasetBriefs, location.search, onAddCoords]);

  return (
    <div className={styles.map}>
      {datasetZones?.length > 0 && (
        <PointLayerMenu>
          <LayerOverview
            legendStartEndValues={legendStartEndValues}
            setLegendStartEndValues={setLegendStartEndValues}
            onMeshControl={setSelectedDatasetId}
            result={meshResults}
            setResult={setMeshResults} />
        </PointLayerMenu>
      )}

      <IconTool
        legendStartEndValues={legendStartEndValues}
        setLegendStartEndValues={setLegendStartEndValues}
        collapseOpen={!showResultCollapse}
        onCollapse={toggleResultCollapse}
        result={meshResults}
        setResult={setMeshResults}
      />

      <ResultPanel
        coordsMoved={coordsMoved}
        onResetCoordsMove={onResetCoordsMoved}
        isMark={isAddMarkerMode}
        open={showResultCollapse}
        onMeshControl={setSelectedDatasetId}
        addCoords={onAddCoords}
        onMark={setAddMarkerMode}
        setMeshUrl={setMeshUrl}
      />

      <Map
        onClick={triggerMapClick}
        layers={layer}
        popupContent={resolvedPopupContent}
        popupCoordinates={activeCoordinates}
        onPopupCoordinatesChanged={handlePopupCoordinatesChanged}
        onClosePopup={handleClosePopup}
      />

      <DeleteConfirmationModal
        visible={confirmationDeletePopup}
        onConfirm={handleDeleteDataset}
        onCancel={() => setConfirmationDeletePopup(false)}
        loading={loadingDelete}
        message="On confirmation the dataset will be removed from MOOD"
      />

      <DeleteConfirmationModal
        visible={notAuthorizedUserPopup}
        onConfirm={handleRequestDeleteDataset}
        onCancel={() => setNotAuthorizedUserPopup(false)}
        loading={loadingDelete}
        message="MIKE MS datasets can only be removed by the project owners, request sent to owners"
      />

      <DeleteConfirmationModal
        visible={confirmationDeletePopupInsitu}
        onConfirm={handleDeleteDatasetInsitu}
        onCancel={() => setConfirmationDeletePopupInsitu(false)}
        loading={loadingDelete}
        message="On confirmation the dataset will be removed from MOOD"
      />

      <SessionExpirationModal
        visible={isModalOpen}
        onLogin={handleLogin}
      />
    </div>
  );
}

